/**
 * Telling a dead link apart from a bad code.
 *
 * A wrong or stale *code* is retryable — resend and try again. A revoked *link*
 * is terminal: no code will ever work, so putting the email form back in front
 * of the guest just sends them round a loop waiting for mail that never arrives
 * (step 1's response is deliberately neutral, so it can't warn them either).
 *
 * Links no longer expire on a clock — revoking is the only kill switch — so the
 * expiry wording below is just belt-and-braces.
 *
 * STOPGAP: `guestQuoteLogin` returns only `success` + a human-readable
 * `message`, so this has to read the prose. That's fragile — it breaks on
 * rewording and on translation. `MessageResponse` in this same schema already
 * carries `messageKey`; the fix is for `GuestQuoteLoginResponse` to do the
 * same (LINK_REVOKED / LINK_EXPIRED / CODE_INVALID / CODE_EXPIRED / …). The
 * key is checked first so that swapping over needs no change here.
 */

export type GuestFailureKind = 'terminal' | 'retryable';

/** Machine-readable keys, once the backend sends them. */
const TERMINAL_KEYS = new Set([
	'LINK_REVOKED',
	'LINK_EXPIRED',
	'GUEST_LINK_REVOKED',
	'GUEST_LINK_EXPIRED',
	'GUEST_ACCESS_REVOKED',
]);

/**
 * Deliberately narrow. A bare "expired" is NOT matched — that's usually the
 * one-time code, which is retryable; only link-specific wording counts.
 */
const TERMINAL_PATTERNS = /revoked|link (?:has )?expired|link is no longer|no longer valid/i;

export function classifyGuestFailure(
	message?: string | null,
	messageKey?: string | null
): GuestFailureKind {
	if (messageKey && TERMINAL_KEYS.has(messageKey.toUpperCase())) return 'terminal';
	if (message && TERMINAL_PATTERNS.test(message)) return 'terminal';
	return 'retryable';
}
